import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface Disponibilite {
  id: string;
  jour_semaine: number;
  heure_debut: string;
  heure_fin: string;
  actif: boolean;
}

const JOURS_SEMAINE = [
  'Dimanche',
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi'
];

export default function DisponibilitesPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [medecinId, setMedecinId] = useState<string | null>(null);
  const [disponibilites, setDisponibilites] = useState<Disponibilite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    jour_semaine: 1,
    heure_debut: '09:00',
    heure_fin: '17:00'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (role !== 'medecin') {
      navigate('/');
      return;
    }
    if (!user?.id) return;
    fetchMedecinData();
  }, [role, navigate, user, authLoading]);

  const fetchMedecinData = async () => {
    setError(null);
    try {
      const { data: medecinData, error: medecinError } = await supabase
        .from('medecins')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (medecinError) throw medecinError;
      
      setMedecinId(medecinData.id);
      await fetchDisponibilites(medecinData.id);
    } catch (err: any) {
      console.error('Erreur:', err);
      setError('Impossible de charger vos disponibilités. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDisponibilites = async (medId: string) => {
    try {
      const { data, error } = await supabase
        .from('disponibilites_medecins')
        .select('*')
        .eq('medecin_id', medId)
        .order('jour_semaine')
        .order('heure_debut');

      if (error) throw error;
      setDisponibilites(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des disponibilités:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!medecinId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('disponibilites_medecins')
        .insert({
          medecin_id: medecinId,
          jour_semaine: formData.jour_semaine,
          heure_debut: formData.heure_debut,
          heure_fin: formData.heure_fin,
          actif: true
        });

      if (error) throw error;

      await fetchDisponibilites(medecinId);
      setShowAddModal(false);
      setFormData({
        jour_semaine: 1,
        heure_debut: '09:00',
        heure_fin: '17:00'
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      alert('Erreur lors de l\'ajout de la disponibilité');
    } finally {
      setSaving(false);
    }
  };

  const toggleDisponibilite = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('disponibilites_medecins')
        .update({ actif: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      
      if (medecinId) {
        await fetchDisponibilites(medecinId);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification');
    }
  };

  const deleteDisponibilite = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette disponibilité ?')) return;

    try {
      const { error } = await supabase
        .from('disponibilites_medecins')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      if (medecinId) {
        await fetchDisponibilites(medecinId);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const groupedDisponibilites = disponibilites.reduce((acc, disp) => {
    if (!acc[disp.jour_semaine]) {
      acc[disp.jour_semaine] = [];
    }
    acc[disp.jour_semaine].push(disp);
    return acc;
  }, {} as Record<number, Disponibilite[]>);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-5xl text-clinic-green animate-spin mb-4"></i>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <i className="ri-error-warning-line text-5xl text-red-500 mb-4"></i>
          <p className="text-gray-800 font-semibold mb-2">Oups, un problème est survenu</p>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => {
              setLoading(true);
              setError(null);
              fetchMedecinData();
            }}
            className="px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-2"></i>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 py-8 min-w-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-clinic-blue-dark mb-2">
                  Mes disponibilités
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">
                  Gérez vos horaires de consultation pour permettre aux patients de prendre rendez-vous
                </p>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 sm:px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2 whitespace-nowrap w-full sm:w-auto justify-center"
              >
                <i className="ri-add-line text-xl"></i>
                <span className="hidden sm:inline">Ajouter une disponibilité</span>
                <span className="sm:hidden">Ajouter</span>
              </button>
            </div>
          </div>

          {/* Disponibilités par jour */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {JOURS_SEMAINE.map((jour, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-100">
                <h3 className="text-xl font-bold text-clinic-blue-dark mb-4 flex items-center gap-2">
                  <i className="ri-calendar-line text-clinic-green"></i>
                  {jour}
                </h3>

                {groupedDisponibilites[index] && groupedDisponibilites[index].length > 0 ? (
                  <div className="space-y-3">
                    {groupedDisponibilites[index].map((disp) => (
                      <div
                        key={disp.id}
                        className={`p-4 rounded-xl border-2 ${
                          disp.actif
                            ? 'bg-clinic-green/5 border-clinic-green/30'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <i className="ri-time-line text-clinic-green"></i>
                            <span className="font-semibold text-clinic-blue-dark">
                              {disp.heure_debut} - {disp.heure_fin}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleDisponibilite(disp.id, disp.actif)}
                              className={`p-2 rounded-lg transition-colors ${
                                disp.actif
                                  ? 'bg-clinic-green text-white hover:bg-clinic-green/90'
                                  : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                              }`}
                              title={disp.actif ? 'Désactiver' : 'Activer'}
                            >
                              <i className={disp.actif ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                            </button>
                            <button
                              onClick={() => deleteDisponibilite(disp.id)}
                              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold ${
                          disp.actif ? 'text-clinic-green' : 'text-gray-500'
                        }`}>
                          {disp.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <i className="ri-calendar-close-line text-4xl mb-2"></i>
                    <p className="text-sm">Aucune disponibilité</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal Ajout */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-clinic-blue-dark">
                Ajouter une disponibilité
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                  Jour de la semaine
                </label>
                <select
                  value={formData.jour_semaine}
                  onChange={(e) => setFormData({ ...formData, jour_semaine: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-colors pr-8 cursor-pointer"
                  required
                >
                  {JOURS_SEMAINE.map((jour, index) => (
                    <option key={index} value={index}>
                      {jour}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                  Heure de début
                </label>
                <input
                  type="time"
                  value={formData.heure_debut}
                  onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-colors"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                  Heure de fin
                </label>
                <input
                  type="time"
                  value={formData.heure_fin}
                  onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-colors"
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Enregistrement...
                    </>
                  ) : (
                    'Ajouter'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
